import { ScanSearch, Target, Eye, Sparkles } from "lucide-react";

const TEAM_IMG =
  "https://images.pexels.com/photos/8117466/pexels-photo-8117466.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TAGS = ["Streaming", "Deep Focus", "Engineering", "Design", "Product"];

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-12 sm:py-16 bg-zinc-950 text-white overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg-dark opacity-60" aria-hidden />
      <div
        aria-hidden
        className="absolute -top-40 right-0 w-[700px] h-[600px] rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle at center, rgba(0,102,255,0.25), transparent 65%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        {/* Row 1 — Full-width headline */}
        <div className="max-w-6xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#6DA6FF]">
            Who We Are
          </p>
          <h4 className="font-display mt-5 text-4xl sm:text-5xl lg:text-[3.75rem] font-bold tracking-[-0.025em] text-white leading-[1.02]">
            Laiv Ai is a product-first technology company focused on building
            intelligent experiences for{" "}
            <span className="text-[#6DA6FF]">professionals</span> around the
            globe.
          </h4>
        </div>

        {/* Row 2 — Image left, paragraph right */}
        <div className="mt-14 grid grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="col-span-12 lg:col-span-6 rounded-3xl overflow-hidden border border-white/10 bg-white/[0.03] relative group">
            <img
              src={TEAM_IMG}
              alt="Laiv Ai team building Vibecast"
              loading="lazy"
              className="w-full h-[380px] lg:h-[480px] object-cover grayscale group-hover:grayscale-0 opacity-80 group-hover:opacity-100 transition-all duration-700"
              data-testid="about-image"
            />
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-zinc-950/70 via-transparent to-transparent"
            />
          </div>

          <div className="col-span-12 lg:col-span-6 lg:pt-2">
            <div
              data-testid="about-description"
              className="text-lg sm:text-xl text-white/75 font-light leading-relaxed space-y-5"
            >
              <p>
                We started with a simple observation: the world&apos;s most
                important professional knowledge is increasingly delivered
                through video, yet the dominant video platforms are optimized
                primarily for entertainment and generalized engagement.
              </p>
              <p>
                <span className="text-white font-medium">Vibecast</span> is
                our first attempt to build something different. A professional
                streaming environment where technology content, discovery and
                product experience are designed around learning intent.
              </p>
              <p>
                As we evolve, our ambition is to build products that reduce
                friction between professionals and the knowledge, tools and
                experiences they need to do better work.
              </p>
            </div>

            {/* Tag chips */}
            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/50">
                <Sparkles className="w-3 h-3 text-[#6DA6FF]" />
                We work across
              </span>
              {TAGS.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full border border-white/15 text-[12px] text-white/80 bg-white/5 hover:border-white/40 hover:text-white transition-colors"
                  data-testid={`about-tag-${t.toLowerCase().replace(" ", "-")}`}
                >
                  <ScanSearch className="w-3 h-3" />
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Row 3 — Mission + Vision cards */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <MiniCard
            testid="mission-card"
            icon={<Target className="w-4 h-4" />}
            label="Our Mission"
            title="Build technology products that help professionals learn, create and work with greater focus."
            body="We believe technology should amplify attention, not compete for it. Our mission is to build purposeful experiences that connect professionals with relevant knowledge and help turn learning into meaningful progress."
          />
          <MiniCard
            testid="vision-card"
            icon={<Eye className="w-4 h-4" />}
            label="Our Vision"
            title="Build the technology ecosystem for focused professional growth."
            body="Starting with professional video, LAIV aims to create products that help people continuously learn, develop expertise and apply knowledge in an increasingly AI-driven world and extend across the full creators workflow."
          />
        </div>
      </div>
    </section>
  );
};

const MiniCard = ({ icon, label, title, body, testid }) => (
  <div
    data-testid={testid}
    className="group rounded-2xl p-7 border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20 hover:-translate-y-1 transition-all duration-300"
  >
    <div className="flex items-center gap-2 text-[#6DA6FF]">
      {icon}
      <span className="text-[11px] font-semibold uppercase tracking-[0.22em]">
        {label}
      </span>
    </div>
    <h3 className="font-display mt-4 text-xl sm:text-2xl font-semibold text-white tracking-tight leading-snug">
      {title}
    </h3>
    <p className="mt-3 text-[15px] text-white/65 leading-relaxed font-light">
      {body}
    </p>
  </div>
);

export default About;
